/**
 * race_season.ts — THE GRAND CIRCUIT RACING SEASON, the pure rules (2026-09-24).
 *
 * MIRRORED byte-identical at server/src/lib/race_season.ts (the harness diffs the two). Both sides read the
 * baked gate table (circuit_gates.generated, its own byte-identical twin), so the server validates a lap
 * against the same twelve gates the client draws, and both sides compute an NPC rival's progress from the
 * same seed — the rivals are never streamed, they are ARITHMETIC every client agrees on.
 *
 *   THE SCHEDULE   a race every RACE_EVERY_S on the wall clock; the grid opens GRID_OPEN_S before the start.
 *                  A queued player who is not on the grid (riding, within GRID_R_M of the start line) when the
 *                  lights go out MISSES the race (Trackmania's Cup of the Day: a start window, not a lobby).
 *   THE LAP        twelve gates in order (gate 0 is the start/finish line); a gate is credited only inside
 *                  GATE_R_M of it and no sooner than the sector's length at RACE_VMAX_MPS — a sector cannot be
 *                  driven faster than any hovercraft flies (server-side sector validation).
 *   THE RIVALS     NPC_ROSTER: named rivals with a pace; a race fills its field to GRID_SIZE with them. Each
 *                  sector's time is the sector's length over the rival's pace × a seeded ±6 % — so a rival has
 *                  good and bad sectors but never rubber-bands (they do not react to the player).
 *   THE SEASON     SEASON_DAYS of UTC days; points by place (the F1 ladder: 25/18/15/12/10/8/6/4/2/1).
 */
import { CIRCUIT_GATES, type CircuitGates } from './circuit_gates.generated';

export const RACE_EVERY_S = 30 * 60;
export const GRID_OPEN_S = 3 * 60;
/** Where a gridded racer must stand (metres from the start line) and be riding when the lights go out. */
export const GRID_R_M = 220;
/** How near a gate a racer must be to be credited with it. */
export const GATE_R_M = 90;
/** No sector may be driven faster than this (the boost ceiling of the best craft, with margin). */
export const RACE_VMAX_MPS = 90;
/** A race nobody finishes ends here; a racer still out is DNF. */
export const RACE_MAX_S = 25 * 60;
export const RACE_LAPS = 1;
export const MAX_PLAYERS = 12;
/** A field is filled with rivals to at least this many cars. */
export const GRID_SIZE = 8;
export const SEASON_DAYS = 7;
export const POINTS = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
/** Finished/cancelled races are kept this long for the results board. */
export const RACE_KEEP_S = 3 * 86_400;

export interface NpcRacer { name: string; pace: number; livery: string }

/** The rivals. Pace = average metres per second over a lap (the corners included). */
export const NPC_ROSTER: NpcRacer[] = [
  { name: 'Vessa Kade', pace: 45.5, livery: '#d6452f' },
  { name: 'Orrin Tallow', pace: 44.8, livery: '#e2b340' },
  { name: 'Mireth Sol', pace: 44.1, livery: '#3fa7d6' },
  { name: 'Dax Harrowgate', pace: 43.6, livery: '#6fbf4a' },
  { name: 'Ilse Varn', pace: 43.0, livery: '#b35fd1' },
  { name: 'Kestrel Ondo', pace: 42.4, livery: '#f07c2a' },
  { name: 'Pell Arrakin', pace: 41.9, livery: '#2fbf9f' },
  { name: 'Suri Blackfen', pace: 41.2, livery: '#c9c9c9' },
  { name: 'Tamsin Rook', pace: 40.6, livery: '#d4476e' },
  { name: 'Gideon Pyre', pace: 40.0, livery: '#8a6f3c' },
  { name: 'Nell Quarrie', pace: 39.3, livery: '#4d6bd6' },
  { name: 'Brask Holloway', pace: 38.7, livery: '#9fbf2f' },
  { name: 'Yara Estmoor', pace: 38.0, livery: '#e06b9a' },
  { name: 'Corvin Lask', pace: 37.2, livery: '#5a5a5a' },
  { name: 'Fenna Druce', pace: 36.5, livery: '#2f8fbf' },
  { name: 'Old Mattock', pace: 35.6, livery: '#bf8f2f' },
];

/** FNV-1a then the murmur3 finalizer (THE HASH LAW: bare FNV is arithmetic in its last character). */
export function raceHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
export function raceH01(s: string): number { return raceHash(s) / 4294967296; }

/** The circuit's gates for a terrain seed, or null (the Grand Circuit exists in the Commons only). */
export function circuitFor(terrainSeed: string): CircuitGates | null {
  return CIRCUIT_GATES[terrainSeed] ?? null;
}

/** The start (unix seconds) of the next race at or after `nowS`, and the one after it. */
export function nextRaceStartS(nowS: number): number {
  return Math.ceil(nowS / RACE_EVERY_S) * RACE_EVERY_S;
}
export function seasonOf(nowS: number): number { return Math.floor(nowS / 86_400 / SEASON_DAYS); }
export function seasonStartS(season: number): number { return season * SEASON_DAYS * 86_400; }

export function pointsFor(place: number): number { return place >= 1 && place <= POINTS.length ? POINTS[place - 1] : 0; }

/** Gates to pass for a finish: every gate once per lap, the last being gate 0 (the line). */
export function gatesToFinish(c: CircuitGates, laps = RACE_LAPS): number { return c.sectors.length * laps; }
/** The gate a racer who has passed `passed` gates must cross next (passes run 1, 2, …, n−1, 0, 1, …). */
export function expectedGate(c: CircuitGates, passed: number): number { return (passed + 1) % c.sectors.length; }
/** Length of the sector that ENDS at `gate` (the one driven to reach it). */
export function sectorLengthInto(c: CircuitGates, gate: number): number {
  const n = c.sectors.length;
  const k = (gate - 1 + n) % n;
  return c.sectors[k][1] - c.sectors[k][0];
}
/** The shortest time (ms) a sector ending at `gate` can honestly take. */
export function minSectorMs(c: CircuitGates, gate: number): number {
  return Math.floor((sectorLengthInto(c, gate) / RACE_VMAX_MPS) * 1000);
}
export function gatePos(c: CircuitGates, gate: number): [number, number] {
  const s = c.sectors[gate % c.sectors.length];
  return [s[2], s[3]];
}

/** The rivals a race fields: `count` distinct roster indices, a deterministic shuffle of the race's seed. */
export function npcPick(raceSeed: string, count: number): number[] {
  const idx = NPC_ROSTER.map((_, i) => i);
  for (let i = idx.length - 1; i > 0; i--) {
    const j = raceHash(`${raceSeed}:pick:${i}`) % (i + 1);
    const t = idx[i]; idx[i] = idx[j]; idx[j] = t;
  }
  return idx.slice(0, Math.max(0, Math.min(count, idx.length)));
}

/** A rival's standing start: it reaches its pace over NPC_LAUNCH_S at a constant acceleration (the grid is a start,
 *  not a flying lap), which costs the first sector half the launch time. */
export const NPC_LAUNCH_S = 4;

/** A rival's sector times (ms) through every sector of every lap — seeded, never reactive. */
export function npcSectorMs(c: CircuitGates, rival: number, raceSeed: string, laps = RACE_LAPS): number[] {
  const pace = NPC_ROSTER[rival]?.pace ?? 40;
  const out: number[] = [];
  const n = c.sectors.length;
  for (let lap = 0; lap < laps; lap++) {
    for (let k = 0; k < n; k++) {
      const len = c.sectors[k][1] - c.sectors[k][0];
      const noise = (raceH01(`${raceSeed}:${rival}:${lap}:${k}`) - 0.5) * 0.12; // ±6 %
      const v = Math.min(RACE_VMAX_MPS * 0.95, pace * (1 + noise));
      out.push(Math.round((len / v + (lap === 0 && k === 0 ? NPC_LAUNCH_S / 2 : 0)) * 1000));
    }
  }
  return out;
}
export function npcFinishMs(c: CircuitGates, rival: number, raceSeed: string, laps = RACE_LAPS): number {
  let t = 0;
  for (const ms of npcSectorMs(c, rival, raceSeed, laps)) t += ms;
  return t;
}
/** Where a rival is `tMs` after the start: arc length (m) along the loop, clamped at the finish (lap-folded). */
export function npcArcAt(c: CircuitGates, rival: number, raceSeed: string, tMs: number, laps = RACE_LAPS): { s: number; lap: number; done: boolean } {
  const secs = npcSectorMs(c, rival, raceSeed, laps);
  const n = c.sectors.length;
  let t = Math.max(0, tMs);
  for (let i = 0; i < secs.length; i++) {
    const k = i % n;
    const [s0, s1] = c.sectors[k];
    if (t < secs[i]) {
      // The standing start: the first sector's distance under a constant acceleration for NPC_LAUNCH_S, then cruising.
      if (i === 0) {
        const T = NPC_LAUNCH_S * 1000, v = (s1 - s0) / (secs[0] - T / 2);
        const d = t < T ? v * t * t / (2 * T) : v * (t - T / 2);
        return { s: s0 + Math.min(s1 - s0, d), lap: 0, done: false };
      }
      return { s: s0 + (s1 - s0) * (t / secs[i]), lap: Math.floor(i / n), done: false };
    }
    t -= secs[i];
  }
  return { s: 0, lap: laps, done: true };
}
